/**
 * MiniMax Image Generation Adapter
 * Supports: text-to-image with aspect ratio control
 * API Docs: https://platform.minimaxi.com/docs/api-reference/image-generation-t2i
 */

import type {
  ImageGenerationConfig,
  ImageGenerationOptions,
  ImageGenerationResult,
} from '../types';

const BASE_URL = 'https://api.minimaxi.com';

export async function generateWithMiniMaxImage(
  config: ImageGenerationConfig,
  options: ImageGenerationOptions,
): Promise<ImageGenerationResult> {
  const baseUrl = (config.baseUrl || BASE_URL).replace(/\/$/, '');

  const model = config.model || 'image-01';

  const aspectRatio = options.aspectRatio || '1:1';

  // baseUrl is admin-configured and trusted as-is (no path normalization).
  // If admin filled a full endpoint URL like https://api.minimaxi.com/v1/image_generation,
  // we use it directly. If admin filled just the host like https://api.minimaxi.com,
  // we append the standard /v1/image_generation path.
  const requestUrl = baseUrl.endsWith('/v1/image_generation') || baseUrl.endsWith('/v1/image_generation/')
    ? baseUrl.replace(/\/$/, '')
    : `${baseUrl}/v1/image_generation`;

  const response = await fetch(requestUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${config.apiKey}`,
      'Content-Type': 'application/json; charset=utf-8',
    },
    body: JSON.stringify({
      model,
      prompt: options.prompt,
      negative_prompt: options.negativePrompt,
      aspect_ratio: aspectRatio,
      response_format: 'url',
      n: 1,
      prompt_optimizer: false,
    }),
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => response.statusText);
    throw new Error(`MiniMax Image API error: ${errText}`);
  }

  const data = await response.json();

  // Check for error response
  if (data?.base_resp?.status_code !== 0 && data?.base_resp?.status_code !== undefined) {
    const code = data.base_resp.status_code;
    const msg = data.base_resp.status_msg || 'unknown error';
    const err = new Error(`MiniMax Image API error ${code}: ${msg}`);
    // 【2026-06-11 修复 SVG NaN 警告】MiniMax Token Plan key 不覆盖 image 端点
    // (2026-06-08 已验证，code 1004 = login fail = 套餐权限问题，不是 key 失效)
    // 抛带 PERMISSION_DENIED 标记的 Error，MediaOrchestrator 识别后静默跳过，
    // 不在控制台刷 error + 不渲染 failed placeholder（避免触发 framer-motion scale(NaN)）
    (err as Error & { __permissionDenied?: boolean }).__permissionDenied =
      code === 1004 || /login fail|auth/i.test(msg);
    throw err;
  }

  const imageUrls = data?.data?.image_urls;
  if (!imageUrls || imageUrls.length === 0) {
    throw new Error(`MiniMax Image: no image URLs returned. Response: ${JSON.stringify(data)}`);
  }

  const imageUrl = imageUrls[0];

  // Determine dimensions from aspect ratio
  let width = options.width || 1024;
  let height = options.height || 1024;
  if (!options.width && !options.height) {
    const [w, h] = aspectRatio.split(':').map(Number);
    if (w && h) {
      if (w > h) {
        width = 1024;
        height = Math.round((1024 * h) / w);
      } else {
        height = 1024;
        width = Math.round((1024 * w) / h);
      }
    }
  }

  return {
    url: imageUrl,
    width,
    height,
  };
}

export async function testMiniMaxImageConnectivity(
  config: ImageGenerationConfig,
): Promise<{ success: boolean; message: string }> {
  try {
    const baseUrl = (config.baseUrl || BASE_URL).replace(/\/$/, '');
    const requestUrl = baseUrl.endsWith('/v1/image_generation') || baseUrl.endsWith('/v1/image_generation/')
      ? baseUrl
      : `${baseUrl}/v1/image_generation`;
    const response = await fetch(requestUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${config.apiKey}`,
        'Content-Type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify({
        model: 'image-01',
        prompt: 'test',
        aspect_ratio: '1:1',
        n: 1,
      }),
    });

    if (response.ok) {
      return { success: true, message: 'MiniMax Image API connected' };
    }

    const errData = await response.json().catch(() => ({}));
    const msg = errData?.base_resp?.status_msg || response.statusText;
    return { success: false, message: `API error: ${msg}` };
  } catch (err) {
    return { success: false, message: `Connection failed: ${(err as Error).message}` };
  }
}
